import type { RefObject } from 'react'

import {
    gsap,
    ms,
    registerGsap,
    useGSAP,
    wfEase
} from '@/lib/gsap'

type RevealVariant = {
    selectors: string[]
    from: gsap.TweenVars
    to: gsap.TweenVars
}

/**
 * Scroll-into-view reveals, recreated from the Webflow SCROLL_INTO_VIEW
 * interactions (IX2 lists a-37, a-38, a-69, a-74, a-75, a-76, a-87, a-93).
 * Each element animates once as it enters the viewport, driven by the same
 * utility class names the original site used.
 */
const variants: RevealVariant[] = [
    {
        selectors: ['.fade-in-on-scroll', '.blog-item-text-header', '.card-item.pricing-card', '.style-guide-empty-container'],
        from: { opacity: 0 },
        to: { opacity: 1, duration: ms(1000), delay: ms(100) }
    },
    {
        selectors: ['.fade-in-move-on-scroll', '.about-image', '.template-includes-box'],
        from: { opacity: 0, y: 25 },
        to: { opacity: 1, y: 0, duration: ms(1000), delay: ms(100) }
    },
    {
        selectors: ['.fade-in-blur-on-scroll'],
        from: { opacity: 0, filter: 'blur(5px)' },
        to: { opacity: 1, filter: 'blur(0px)', duration: ms(1000), delay: ms(100) }
    },
    {
        selectors: ['.fade-in-slace-on-scroll'],
        from: { opacity: 0, scale: 1.1 },
        to: { opacity: 1, scale: 1, duration: ms(1000), delay: ms(100) }
    },
    {
        selectors: ['.project-item', '.project-item-style-2'],
        from: { opacity: 0, y: 20 },
        to: { opacity: 1, y: 0, duration: ms(1200) }
    },
    {
        selectors: ['.post-item', '.team-item'],
        from: { opacity: 0 },
        to: { opacity: 1, duration: ms(1500), delay: ms(100) }
    },
    {
        selectors: ['.text-center-2'],
        from: { opacity: 0 },
        to: { opacity: 1, duration: ms(1000), delay: ms(100) }
    }
]

export const useScrollReveal = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(
        () => {
            registerGsap()

            const root = scope.current
            if (!root) return

            const ease = wfEase('ease')

            variants.forEach(({ selectors, from, to }) => {
                const elements = root.querySelectorAll<HTMLElement>(selectors.join(', '))

                elements.forEach(element => {
                    gsap.set(element, from)
                    gsap.to(element, {
                        ...to,
                        ease,
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 88%',
                            once: true
                        }
                    })
                })
            })
        },
        { scope }
    )
}
